/**
 * Property card for the Owner Portal "My properties" grid.
 *
 * Design: diagonal-stripe placeholder image (or real cover photo) with
 * city overlay, "N overdue" pill badge, property title + arrow, full
 * address, and ROOMS / OCCUPANCY / YIELD stats row.
 *
 * Clicking the card opens the owner's own read-only property page
 * (/admin/owner/property/:id) — the same view the analytics area links to,
 * not the admin property detail. `?from=properties` only tells that page to
 * point its breadcrumb back here instead of at Analytics.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import type { PropertyListItem } from "@/types/property";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatAddress } from "@/lib/v2-format";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { usePropertyReminderStats } from "@/hooks/v2/usePropertyReminderStats";
import { useOwnerPortalV3 } from "@/hooks/owner/useOwnerPortalV3";
import {
  useStartOwnerContract,
  type StartContractError,
} from "@/hooks/owner/useStartOwnerContract";

type Props = {
  property: PropertyListItem;
  /** Yield % from owner_portfolio_dashboard RPC, passed in by parent. */
  yieldPct?: number | null;
  /**
   * False while this property hasn't finished onboarding — no property_owners
   * link yet, or a required CoLiviq management contract that isn't signed
   * (`owner_visible_properties`). The card still appears, blurred behind a
   * "Yet to be onboarded" tag, and doesn't navigate: an owner needs to see
   * that CoLiviq knows about the building even before it goes live. Defaults
   * to true so callers that don't pass it see the normal card.
   */
  onboarded?: boolean;
  /**
   * WHICH kind of not-onboarded, for the V3 card. The two are opposites:
   * `awaiting_contract` is waiting on the OWNER and offers them the wizard;
   * `in_review` is waiting on CoLiviq and must offer no button at all, or an
   * owner who has already sent their documents is asked to send them again.
   *
   * Only read when `onboarded` is false and the V3 shell is on. Omitted, a
   * not-onboarded card falls back to awaiting_contract — the state the
   * database reported for both before 20261012085005.
   */
  pendingState?: "awaiting_contract" | "in_review";
};

/**
 * What to tell the owner when the sign button fails, by reason.
 *
 * Every non-"already started" cause used to collapse into one generic
 * "that didn't work, try again" line (COL-528 review, 14 Oct) -- which looks
 * identical whether the account has no email on file, the property no longer
 * belongs to this session, or something genuinely broke server-side. An owner
 * (or whoever is reading a screenshot of one) could not tell which. Each
 * reason now gets its own line and, where there is something the owner can
 * actually do about it, its own instruction.
 */
function signErrorCopy(
  error: StartContractError | null,
  t: (key: string, fallback?: string) => string
): string {
  switch (error) {
    case "not_awaiting_contract":
      return t(
        "owner.card.signAlreadyStarted",
        "Dieser Vertrag läuft bereits – laden Sie die Seite neu."
      );
    case "no_email_on_account":
      return t(
        "owner.card.signNoEmail",
        "Für Ihr Konto ist keine E-Mail-Adresse hinterlegt. Bitte melden Sie sich bei uns, damit wir das ergänzen können."
      );
    case "not_your_property":
      return t(
        "owner.card.signNotYourProperty",
        "Diese Immobilie konnte gerade nicht zugeordnet werden. Laden Sie die Seite neu; falls das bestehen bleibt, melden Sie sich bei uns."
      );
    default:
      return t(
        "owner.card.signFailed",
        "Das hat nicht geklappt. Bitte versuchen Sie es erneut."
      );
  }
}

export function OwnerPropertyCard({
  property: p,
  yieldPct,
  onboarded = true,
  pendingState = "awaiting_contract",
}: Props) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [showPendingInfo, setShowPendingInfo] = useState(false);
  const v3 = useOwnerPortalV3();
  // Shared with the sidebar card: one route into a legal document.
  const start = useStartOwnerContract();
  const inReview = !onboarded && v3 && pendingState === "in_review";

  const occupancyPct =
    p.rooms_count > 0
      ? Math.round((p.occupied_count / p.rooms_count) * 100)
      : null;

  // Reminders badge: current-period overdue rows + open past-month rows
  // (COL-435), from the shared hook so this count matches the property's
  // Reminders tab and the other property cards.
  const { data: reminderStats, isError: reminderStatsError } =
    usePropertyReminderStats(p.id, {
      propertyRemindersEnabled: p.rent_reminders_enabled,
      // Nothing on a blurred card is readable — skip the fetch until it's live.
      enabled: onboarded,
    });
  const overdueCount = reminderStats?.total ?? 0;

  function handleActivate() {
    if (onboarded) {
      navigate(`/admin/owner/property/${p.id}?from=properties`);
    } else if (!inReview) {
      // "Not clickable" in the PRD is about the review state specifically:
      // there is nothing for the owner to do, so a dialog explaining that
      // would be a click that leads to a shrug.
      setShowPendingInfo(true);
    }
  }

  return (
    <>
      <div
        role="link"
        aria-disabled={!onboarded}
        aria-label={
          onboarded
            ? undefined
            : `${p.title} — ${
                inReview
                  ? t("owner.card.underReviewTag", "Under coliviq review")
                  : v3
                    ? t("owner.card.onboardingPendingTag", "Onboarding pending")
                    : t("owner.card.yetToBeOnboarded", "Yet to be onboarded")
              }`
        }
        // Nothing to activate on a card in review, so it is not a tab stop.
        tabIndex={inReview ? -1 : 0}
        onClick={handleActivate}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleActivate();
        }}
        className={cn(
          "relative rounded-2xl overflow-hidden border bg-white transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          inReview ? "cursor-default" : "cursor-pointer",
          onboarded
            ? "border-gray-200 hover:shadow-lg"
            : "border-dashed border-gray-300"
        )}
      >
        {/* Not-onboarded overlay. It sits OUTSIDE the blurred wrapper below so
            the tag and the property's name stay sharp — the blur is there to
            say "these figures aren't real yet", not to hide which building
            this is. aria-hidden because the card itself already carries the
            same words in its aria-label. */}
        {!onboarded && v3 && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2.5 bg-white/55 px-6 text-center">
            <span
              className="absolute right-3 top-3 rounded-full px-3 py-1 text-[11px] font-semibold"
              style={
                inReview
                  ? { background: "rgba(232,168,124,0.28)", color: "#8A6B54" }
                  : { background: "#0F4C4A", color: "#FFFFFF" }
              }
            >
              {inReview
                ? t("owner.card.underReviewTag", "Under coliviq review")
                : t("owner.card.onboardingPendingTag", "Onboarding pending")}
            </span>
            <p className="text-sm font-semibold text-[#0F4C4A] line-clamp-1">
              {p.title}
            </p>
            {inReview ? (
              <p className="max-w-[260px] text-xs leading-relaxed text-[#47605E]">
                {t(
                  "owner.card.underReviewBody",
                  "Your management contract has arrived. coliviq is reviewing the documents and will activate the property, usually within 2 working days."
                )}
              </p>
            ) : (
              <>
                <p className="text-xs text-[#7A8E8C]">
                  {t("owner.card.signTakesMinutes", "Takes about 5 minutes")}
                </p>
                <button
                  type="button"
                  onClick={(e) => {
                    // The card behind this is a role="link"; without stopping
                    // here the button's click would also activate it.
                    e.stopPropagation();
                    start.mutate(Number(p.id));
                  }}
                  disabled={start.isPending}
                  className="mt-0.5 rounded-full px-4 py-2 text-xs font-bold transition-colors disabled:opacity-60"
                  style={{ background: "#2FB3AA", color: "#06201F" }}
                >
                  {t("owner.card.signContractNow", "Sign contract now")} →
                </button>
                {start.isError && (
                  <p
                    className="text-[11px]"
                    style={{ color: "#8A6B54" }}
                    role="alert"
                  >
                    {signErrorCopy(start.error, t)}
                  </p>
                )}
              </>
            )}
          </div>
        )}
        {!onboarded && !v3 && (
          <div
            aria-hidden
            className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-white/50 px-6 text-center"
          >
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white shadow-sm">
              <Clock className="h-3.5 w-3.5" />
              {t("owner.card.yetToBeOnboarded", "Yet to be onboarded")}
            </span>
            <p className="text-sm font-semibold text-gray-900 line-clamp-1">
              {p.title}
            </p>
            <p className="text-xs text-gray-500 line-clamp-1">{p.city}</p>
          </div>
        )}

        <div
          className={cn(
            !onboarded && "pointer-events-none select-none blur-[3px]"
          )}
        >
          {/* ── Image / placeholder area ─────────────────────────────────── */}
          <div
            className="relative h-36"
            style={{
              backgroundImage:
                "repeating-linear-gradient(-45deg, #e5e7eb 0px, #e5e7eb 1px, transparent 1px, transparent 12px)",
              backgroundColor: "#f3f4f6",
            }}
          >
            {p.cover_image ? (
              <img
                src={p.cover_image}
                alt={p.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : (
              /* City name centred over the stripe pattern */
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-semibold tracking-[0.2em] text-gray-400 uppercase select-none">
                  {p.city}
                </span>
              </div>
            )}

            {/* Overdue badge. Not rendered while the card is blurred — the
              overlay above carries the only status this property has. */}
            {onboarded && reminderStatsError && (
              <div
                className="absolute top-3 right-3 bg-muted text-muted-foreground text-xs font-semibold px-2.5 py-1 rounded-full shadow-sm"
                title="Could not load reminders"
              >
                —
              </div>
            )}
            {onboarded && !reminderStatsError && overdueCount > 0 && (
              <div
                className={cn(
                  "absolute top-3 right-3 text-xs font-semibold px-2.5 py-1 rounded-full shadow-sm text-white",
                  v3 ? "bg-[#C0714F]" : "bg-red-500"
                )}
              >
                {overdueCount} {t("owner.card.overdue", "overdue")}
              </div>
            )}
          </div>

          {/* ── Card body ────────────────────────────────────────────────── */}
          <div className="p-4">
            {/* Title + arrow */}
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className="font-semibold text-gray-900 leading-snug line-clamp-1">
                {p.title}
              </h3>
              {onboarded && (
                <ArrowRight className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
              )}
            </div>

            {/* Full address */}
            <p className="text-sm text-gray-500 mb-4 line-clamp-1">
              {formatAddress(p.address, p.zip_code, p.city)}
            </p>

            {/* Stats row */}
            <div className="flex gap-5">
              <div>
                <p className="text-[10px] font-semibold tracking-wider text-gray-400 uppercase mb-0.5">
                  {t("owner.card.rooms", "Rooms")}
                </p>
                <p className="text-sm font-semibold text-gray-900">
                  {p.occupied_count}/{p.rooms_count}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-semibold tracking-wider text-gray-400 uppercase mb-0.5">
                  {t("owner.card.occupancy", "Occupancy")}
                </p>
                <p className="text-sm font-semibold text-gray-900">
                  {occupancyPct != null ? `${occupancyPct}%` : "—"}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-semibold tracking-wider text-gray-400 uppercase mb-0.5">
                  {t("owner.card.yield", "Yield")}
                </p>
                <p
                  className={
                    yieldPct != null
                      ? "text-sm font-semibold text-[#2dd4bf]"
                      : "text-sm font-semibold text-gray-400"
                  }
                >
                  {yieldPct != null ? `${Number(yieldPct).toFixed(2)}%` : "—"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={showPendingInfo} onOpenChange={setShowPendingInfo}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                "owner.card.pendingInfoTitle",
                "This property isn't onboarded yet"
              )}
            </AlertDialogTitle>
            {/* Covers both reasons a card is blurred — an unsigned management
                contract, and a property coliviq hasn't finished linking to
                this account — because the owner can't tell which applies. */}
            <AlertDialogDescription>
              {t(
                "owner.card.pendingInfoDesc",
                "coliviq is still setting this property up. If we've sent you a management contract, check your email for the signing link — otherwise get in touch and we'll finish the onboarding."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex justify-end gap-2">
            <a
              href="mailto:support@coliviq.de?subject=Property%20onboarding%20link"
              className="inline-flex items-center rounded-xl border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t("owner.card.contactSupport", "Contact support")}
            </a>
            <AlertDialogAction>
              {t("owner.card.pendingInfoClose", "Got it")}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
